import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Select,
  Space,
  TimePicker,
} from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import CustomButton from 'components/custom-button';
import CustomizedUpload from 'components/customized-upload';
import { REGEX_USERNAME, ROLE_USER, UPLOAD_TYPE } from 'constants/common';
import { REQUIRED } from 'constants/rule-form';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { CHECK_FILE } from 'utils/rule-form';
import DebounceSelect from '../../../components/debounce-select/select';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import moment from 'moment';
import { SERVER_FILE_ENDPOINT } from '../../../constants/envs';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import styles from '../../news-manager/styles.module.scss';
import { editorConfig } from '../../news-manager/component/ModalCreateEdit';
import JoditEditor from 'jodit-react';
import { MEMBER_ROLE_LABELS } from '../../user-manager/membership-labels';

/** Vai trò chọn được cho hạn mức phiếu (khớp Role ở backend, resolveVoteLimit). */
const VOTE_LIMIT_ROLES = ['USER', 'CREATOR', 'STUDIO_CREATOR', 'AGENCY', 'MOD', 'ADMIN'];

/** Lỗi backend (message) → câu dễ hiểu cho người quản trị. */
const VOTE_ERROR_TEXT: Record<string, string> = {
  // Backend cũ (trước 02/10) chỉ cho 1 sự kiện.
  EXIT_VOTE_RUNNING:
    'Đang có một sự kiện bình chọn khác. Hệ thống chỉ chạy 1 sự kiện mỗi lúc — hãy xóa sự kiện đó trong danh sách rồi tạo lại.',
  TOO_MANY_ACTIVE_VOTES:
    'Đã có 5 sự kiện chưa kết thúc (tối đa 5, tính cả sắp diễn ra và đang tạm tắt). Hãy xoá bớt hoặc chờ một sự kiện kết thúc.',
};

const PRIORITY_OPTIONS = [
  { value: 5, label: '5 — Cao nhất' },
  { value: 4, label: '4 — Cao' },
  { value: 3, label: '3 — Bình thường' },
  { value: 2, label: '2 — Thấp' },
  { value: 1, label: '1 — Thấp nhất' },
];

const voteErrorText = (e: any) =>
  VOTE_ERROR_TEXT[e?.message] ||
  (typeof e?.message === 'string' && !/^[A-Z_]+$/.test(e.message) ? e.message : '') ||
  'Có lỗi xảy ra, vui lòng thử lại.';

interface Props {
  isOpen: boolean;
  handleCancelCreateEditModal: () => void;
  onChangeStatusUpdateData: () => void;
  valueUserSelected: any;
}

// eslint-disable-next-line arrow-body-style
// Can not select days before today and today
const disabledDate: RangePickerProps['disabledDate'] = (current) => {
  return current && current.add(1, 'day') < dayjs().endOf('day');
};

const disabledDateTime = () => ({
  // disabledHours: () => range(0, 24).splice(0, moment().hours() + 1),
  // disabledMinutes: () => range(0, 60),
  // disabledSeconds: () => [55, 56],
});

const ModalCreateAndEditVote = ({
  isOpen,
  valueUserSelected,
  handleCancelCreateEditModal,
  onChangeStatusUpdateData,
}: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [ruleVote, setRuleVote] = useState<any>('');
  const [introduction, setIntroduction] = useState<any>('');
  // Đã gõ vào trình soạn Giới thiệu / Thể lệ (form.isFieldsTouched không thấy 2 ô này).
  const editorDirty = useRef(false);
  const chosenRoles: string[] = (Form.useWatch('vote_settings', form) || [])
    .map((x: any) => x?.role)
    .filter(Boolean);

  const onBlurContent = useCallback(
    (newContent) => {
      setRuleVote(newContent);
    },
    [setRuleVote]
  );

  const onBlurIntroduction = useCallback(
    (newContent) => {
      setIntroduction(newContent);
    },
    [setIntroduction]
  );

  const onChange = useCallback(
    (newContent) => {
      if (newContent !== ruleVote) editorDirty.current = true;
    },
    [ruleVote]
  );

  const onChangeIntroduction = useCallback(
    (newContent) => {
      if (newContent !== introduction) editorDirty.current = true;
    },
    [introduction]
  );

  /** Đóng hẳn: xoá form để lần mở sau (Thêm hoặc Sửa) không dính dữ liệu cũ. */
  const closeModal = useCallback(() => {
    form.resetFields();
    setRuleVote('');
    setIntroduction('');
    editorDirty.current = false;
    handleCancelCreateEditModal();
  }, [form, handleCancelCreateEditModal]);

  /** Bấm ra ngoài / X / Esc / Hủy bỏ: đã nhập gì thì hỏi lại, không mất dữ liệu vì lỡ tay. */
  const requestClose = useCallback(() => {
    if (!form.isFieldsTouched() && !editorDirty.current) {
      closeModal();
      return;
    }
    Modal.confirm({
      title: 'Thoát mà không lưu?',
      content: 'Thông tin bạn vừa nhập cho sự kiện sẽ bị mất.',
      okText: 'Thoát',
      okButtonProps: { danger: true },
      cancelText: 'Tiếp tục nhập',
      onOk: closeModal,
    });
  }, [form, closeModal]);

  useEffect(() => {
    if (valueUserSelected) {
      form.setFieldsValue({
        title: valueUserSelected?.title,
        score: valueUserSelected.score,
        background: valueUserSelected?.background
          ? {
              fileKey: valueUserSelected?.background,
              fileUrl: `${SERVER_FILE_ENDPOINT}/${valueUserSelected.background}`,
            }
          : null,
        startDate: dayjs(valueUserSelected?.startDate),
        endDate: dayjs(valueUserSelected?.endDate),
        isVoteDisabled: valueUserSelected?.isVoteDisabled || false,
        isFeatured: !!valueUserSelected?.isFeatured,
        priority: valueUserSelected?.priority || 3,
        startTimeVote: dayjs(valueUserSelected?.startTimeVote, 'HH:mm'),
        endTimeVote: dayjs(valueUserSelected?.endTimeVote, 'HH:mm'),
        // Bản cũ cho gõ tay vai trò ("user") → chuẩn hoá chữ hoa để khớp danh sách chọn.
        vote_settings: (valueUserSelected?.vote_settings || []).map((x: any) => ({
          ...x,
          role: String(x?.role || '')
            .trim()
            .toUpperCase(),
        })),
      });
      setRuleVote(valueUserSelected?.ruleVote || '');
      setIntroduction(valueUserSelected?.introduction || '');
    }
  }, [form, valueUserSelected]);

  const onFinish = async (values: any) => {
    if (dayjs(values.endDate).isBefore(dayjs(values.startDate), 'day')) {
      notification.error({ message: 'Ngày kết thúc phải từ ngày bắt đầu trở đi.' });
      return;
    }
    // Giờ là khung vote MỖI NGÀY: 00:00 → 00:00 dài 0 phút, sự kiện không nhận phiếu nào.
    const minuteOf = (t: any) => dayjs(t).hour() * 60 + dayjs(t).minute();
    if (minuteOf(values.endTimeVote) <= minuteOf(values.startTimeVote)) {
      notification.error({
        message: 'Giờ kết thúc bình chọn phải sau giờ bắt đầu.',
        description: 'Đây là khung giờ vote mỗi ngày. Muốn vote cả ngày: 00:00 – 23:59.',
      });
      return;
    }

    if (values.startTimeVote) {
      values.startTimeVote = dayjs(values.startTimeVote).format('HH:mm');
    }

    if (values.endTimeVote) {
      values.endTimeVote = dayjs(values.endTimeVote).format('HH:mm');
    }

    values.isVoteDisabled = values?.isVoteDisabled || false;
    values.isFeatured = !!values?.isFeatured;
    values.priority = values?.priority || 3;

    values.ruleVote = ruleVote;
    values.introduction = introduction;
    values.background = values?.background?.fileKey;

    setLoading(true);
    if (valueUserSelected?.id) {
      try {
        await AxiosUtils.put(`${API.updateVote}/${valueUserSelected.id}`, values);
        closeModal();
        onChangeStatusUpdateData();
        notification.success({
          message: 'Thành công',
          description: 'Sửa thành công!',
          duration: 4,
        });
      } catch (e) {
        notification.error({
          message: 'Không thành công',
          description: voteErrorText(e),
          duration: 6,
        });
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const res: any = await AxiosUtils.post(API.voteCreate, values);
        if (res?.status === 201) {
          closeModal();
          onChangeStatusUpdateData();
          notification.success({
            message: 'Thành công',
            description: 'Tạo thành công!',
            duration: 4,
          });
        }
      } catch (e) {
        notification.error({
          message: 'Không thành công',
          description: voteErrorText(e),
          duration: 6,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <Modal
        forceRender
        open={isOpen}
        title={valueUserSelected?.id ? 'Sửa sự kiện bình chọn' : 'Thêm sự kiện bình chọn'}
        onCancel={requestClose}
        width={800}
        getContainer={false}
        footer={false}
      >
        <Form
          name="form"
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          onFinish={onFinish}
          autoComplete="off"
        >
          <label htmlFor="title">Tiêu đề sự kiện</label>
          <Form.Item rules={[REQUIRED]} name="title" wrapperCol={{ span: 24 }}>
            <Input size={'middle'} placeholder="Nhập tiêu đề sự kiện" />
          </Form.Item>
          <label className={styles.labelInput} htmlFor="background">
            Ảnh nền
          </label>
          <Form.Item name="background" wrapperCol={{ span: 24 }} rules={[CHECK_FILE]}>
            <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={5} />
          </Form.Item>
          <div className="d-flex">
            <div>
              <label htmlFor="startDate">Ngày bắt đầu</label>
              <Form.Item rules={[REQUIRED]} name="startDate" wrapperCol={{ span: 24 }}>
                <DatePicker
                  format="YYYY-MM-DD"
                  disabledDate={disabledDate}
                  disabledTime={disabledDateTime}
                />
              </Form.Item>
            </div>
            <div style={{ marginLeft: 50 }}>
              <label htmlFor="startTimeVote">Giờ bắt đầu bình chọn</label>
              <Form.Item rules={[REQUIRED]} name="startTimeVote" wrapperCol={{ span: 24 }}>
                <TimePicker format={'HH:mm'} showSecond={false} />
              </Form.Item>
            </div>
            <div style={{ marginLeft: 50 }}>
              <label htmlFor="endDate">Ngày kết thúc</label>
              <Form.Item rules={[REQUIRED]} name="endDate" wrapperCol={{ span: 24 }}>
                <DatePicker
                  format="YYYY-MM-DD"
                  disabledDate={disabledDate}
                  disabledTime={disabledDateTime}
                />
              </Form.Item>
            </div>
            <div style={{ marginLeft: 50 }}>
              <label htmlFor="endTimeVote">Giờ kết thúc bình chọn</label>
              <Form.Item rules={[REQUIRED]} name="endTimeVote" wrapperCol={{ span: 24 }}>
                <TimePicker format={'HH:mm'} showSecond={false} />
              </Form.Item>
            </div>
          </div>
          <>
            <label className={styles.labelInput} htmlFor="score">
              Điểm cộng cho người chơi mỗi lượt bình chọn
            </label>
            <Form.Item name="score" rules={[REQUIRED]} wrapperCol={{ span: 16 }}>
              <InputNumber
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                placeholder="Nhập số điểm"
                size="middle"
                style={{ width: 300 }}
              />
            </Form.Item>
          </>
          <label className={styles.labelInput}>Số lượt bình chọn tối đa theo vai trò</label>
          <Form.List name="vote_settings">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="start">
                    <Form.Item
                      {...restField}
                      wrapperCol={{ span: 24 }}
                      name={[name, 'role']}
                      rules={[{ required: true, message: 'Chọn vai trò' }]}
                    >
                      <Select
                        placeholder="Chọn vai trò"
                        style={{ width: 220 }}
                        options={VOTE_LIMIT_ROLES.map((role) => ({
                          value: role,
                          label: MEMBER_ROLE_LABELS[role] || role,
                          // Mỗi vai trò một dòng: đã chọn ở dòng khác thì mờ đi.
                          disabled:
                            chosenRoles.includes(role) &&
                            form.getFieldValue(['vote_settings', name, 'role']) !== role,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      wrapperCol={{ span: 24 }}
                      name={[name, 'total']}
                      rules={[{ required: true, message: 'Nhập số lượt' }]}
                    >
                      <InputNumber
                        min={0}
                        precision={0}
                        placeholder="Số lượt tối đa"
                        suffix="phiếu"
                        style={{ width: 200 }}
                      />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Form.Item
                  wrapperCol={{ span: 24 }}
                  extra="Vai trò không có trong danh sách dùng hạn mức của User. Số phiếu tính cho cả sự kiện."
                >
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={fields.length >= VOTE_LIMIT_ROLES.length}
                  >
                    Thêm vai trò
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <label className={styles.labelInput}>Hiển thị khi nhiều sự kiện chạy cùng lúc</label>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="isFeatured"
                valuePropName="checked"
                wrapperCol={{ span: 24 }}
                extra="Đứng đầu danh sách trên app; là sự kiện app bản cũ hiển thị."
              >
                <Checkbox>Nổi bật</Checkbox>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                wrapperCol={{ span: 24 }}
                extra="Cao hơn thì đứng trước và hiện lâu hơn ở thẻ Trang chủ."
              >
                <Select options={PRIORITY_OPTIONS} style={{ width: '100%' }} prefix="Ưu tiên" />
              </Form.Item>
            </Col>
          </Row>

          <Col span={16}>
            <Form.Item name="isVoteDisabled" valuePropName="checked">
              <Checkbox>Tạm tắt sự kiện (không hiện trên app, không nhận phiếu)</Checkbox>
            </Form.Item>
          </Col>
          <br />

          <label className={styles.labelInput} htmlFor="ruleVote">
            Giới thiệu
          </label>
          <div
            style={{
              maxWidth: editorConfig.width,
              margin: '0 auto',
              marginBottom: 10,
            }}
          >
            <JoditEditor
              value={introduction}
              config={editorConfig as any}
              onBlur={onBlurIntroduction}
              onChange={onChangeIntroduction}
            />
          </div>

          <label className={styles.labelInput} htmlFor="ruleVote">
            Thể lệ
          </label>
          <div
            style={{
              maxWidth: editorConfig.width,
              margin: '0 auto',
              marginBottom: 10,
            }}
          >
            <JoditEditor
              value={ruleVote}
              config={editorConfig as any}
              onBlur={onBlurContent}
              onChange={onChange}
            />
          </div>

          <div className="d-flex justify-center">
            <Space size="large">
              <CustomButton
                bgColor="secondary-green"
                text="Lưu"
                htmlType="submit"
                loading={loading}
              />
              <CustomButton
                text="Hủy Bỏ"
                bgColor="primary-grey"
                htmlType="button"
                onClick={requestClose}
              />
            </Space>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ModalCreateAndEditVote;
